import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { X, Send, Loader2, CheckCheck, Check } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useVisualViewport, TypingBubble } from "@/components/price/ChatSheet";

const fmtTime = (iso) => new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });

// Chat support Price (utilisateur ↔ équipe) : multi-ligne, « écrit… », Envoyé/Vu, clavier mobile.
export default function SupportChat({ title, kicker = "Support Price", loadUrl, sendUrl, typingUrl, mine, onClose, testPrefix, emptyText }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [typing, setTyping] = useState([]);
  const [otherReadAt, setOtherReadAt] = useState(null);
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const atBottomRef = useRef(true);
  const lastTypingRef = useRef(0);
  const countRef = useRef(0);
  const vp = useVisualViewport(true);

  const scrollToBottom = useCallback((smooth = true) => {
    const el = listRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: smooth ? "smooth" : "auto" });
  }, []);

  const load = useCallback(async () => {
    try {
      const r = await api.get(loadUrl);
      const items = r.data?.messages || [];
      setTyping(Array.isArray(r.data?.typing) ? r.data.typing : []);
      setOtherReadAt(r.data?.other_read_at || null);
      setMessages((prev) => {
        const ids = new Set(items.map((i) => i.id));
        const pending = prev.filter((m) => m._optimistic || (!ids.has(m.id) && m.sender === mine && Date.now() - new Date(m.created_at).getTime() < 10000));
        return [...items, ...pending];
      });
      if (items.length !== countRef.current) {
        if (countRef.current && items[items.length - 1]?.sender !== mine) {
          try { navigator.vibrate?.(45); } catch { /* ignore */ }
        }
        countRef.current = items.length;
        setTimeout(() => scrollToBottom(true), 30);
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [loadUrl, mine, scrollToBottom]);

  useEffect(() => {
    load();
    const t = setInterval(() => { if (!document.hidden) load(); }, 2500);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    const { overflow, position, width } = document.body.style;
    const y = window.scrollY;
    Object.assign(document.body.style, { overflow: "hidden", position: "fixed", width: "100%", top: `-${y}px` });
    return () => { Object.assign(document.body.style, { overflow, position, width, top: "" }); window.scrollTo(0, y); };
  }, []);

  useEffect(() => {
    const el = listRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const onScroll = () => { atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 60; };
    const ro = new ResizeObserver(() => { if (atBottomRef.current) el.scrollTop = el.scrollHeight; });
    el.addEventListener("scroll", onScroll, { passive: true });
    ro.observe(el);
    return () => { ro.disconnect(); el.removeEventListener("scroll", onScroll); };
  }, [loading]);
  useEffect(() => { setTimeout(() => scrollToBottom(false), 200); }, [vp.height, vp.kb, scrollToBottom]);
  useEffect(() => { if (typing.length) setTimeout(() => scrollToBottom(true), 30); }, [typing.length, scrollToBottom]);

  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  }, [input]);

  const pingTyping = () => {
    const now = Date.now();
    if (now - lastTypingRef.current < 2500) return;
    lastTypingRef.current = now;
    api.post(typingUrl).catch(() => {});
  };

  const send = async () => {
    const t = input.trim();
    if (!t || sending) return;
    setSending(true);
    const tmp = { id: `local-${Date.now()}`, sender: mine, body: t, created_at: new Date().toISOString(), _optimistic: true };
    setMessages((prev) => [...prev, tmp]);
    setInput("");
    lastTypingRef.current = 0;
    setTimeout(() => scrollToBottom(true), 30);
    try {
      const r = await api.post(sendUrl, { body: t });
      countRef.current += 1;
      setMessages((prev) => prev.map((m) => (m.id === tmp.id ? r.data.message : m)));
    } catch (e) {
      toast.error(formatApiError(e));
      setMessages((prev) => prev.filter((m) => m.id !== tmp.id));
      setInput(t);
    } finally { setSending(false); }
  };

  const lastMineId = [...messages].reverse().find((m) => m.sender === mine)?.id;
  const statusOf = (m) => {
    if (m.id !== lastMineId) return null;
    if (m._optimistic) return "sending";
    return (m.read_at || (otherReadAt && otherReadAt >= m.created_at)) ? "seen" : "sent";
  };
  const sheetHeight = vp.kb ? vp.height - 8 : Math.min(vp.height * 0.92, vp.height - 16);
  const typer = typing[0]?.name;

  return (
    <div
      className="fixed inset-x-0 z-[95] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm"
      style={{ top: vp.top, height: vp.height }}
      onClick={onClose}
      data-testid={`${testPrefix}-window`}
    >
      <div
        className={"w-full sm:max-w-md bg-[#E8EAD7] border-2 border-black shadow-[0_8px_0_0_#000] flex flex-col overflow-hidden " + (vp.kb ? "rounded-3xl mx-1 mb-1" : "rounded-t-3xl sm:rounded-3xl")}
        style={{ height: sheetHeight, transition: "height 160ms ease-out" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex-none flex items-center gap-2 px-4 py-3 border-b-2 border-black bg-primary text-primary-foreground">
          <button type="button" onClick={onClose} data-testid={`${testPrefix}-close`} aria-label="Fermer" className="w-9 h-9 grid place-items-center rounded-xl bg-white/20 active:translate-y-[1px]">
            <X className="w-4 h-4" strokeWidth={3} />
          </button>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-black uppercase tracking-widest opacity-80">{kicker}</div>
            <div className="font-[Outfit] text-[15px] font-black leading-none truncate">{title}</div>
            <div className="font-[Outfit] text-[11px] mt-1 truncate opacity-90" data-testid={`${testPrefix}-header-status`}>
              {typer ? `${typer} est en train d'écrire…` : "En ligne sur Price"}
            </div>
          </div>
        </div>

        <div ref={listRef} className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-3 space-y-2" style={{ WebkitOverflowScrolling: "touch" }} data-testid={`${testPrefix}-messages`}>
          {loading && <div className="py-8 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>}
          {!loading && messages.length === 0 && <div className="py-10 text-center text-[13px] font-bold text-foreground/60">{emptyText}</div>}
          {messages.map((m) => <SupportBubble key={m.id} m={m} mine={m.sender === mine} status={statusOf(m)} testPrefix={testPrefix} />)}
          {typer && <TypingBubble name={typer} />}
        </div>

        <form
          onSubmit={(e) => { e.preventDefault(); send(); }}
          className="flex-none border-t-2 border-black bg-white px-3 py-2.5 flex items-end gap-2"
          style={{ paddingBottom: vp.kb ? 10 : "max(10px, env(safe-area-inset-bottom))" }}
        >
          <textarea
            ref={inputRef}
            rows={1}
            value={input}
            onChange={(e) => { setInput(e.target.value); if (e.target.value.trim()) pingTyping(); }}
            placeholder="Écris un message…"
            maxLength={2000}
            enterKeyHint="enter"
            data-testid={`${testPrefix}-input`}
            className="flex-1 min-h-[44px] max-h-[120px] resize-none rounded-xl border-2 border-black px-3 py-2.5 font-[Outfit] font-medium leading-[1.35] bg-[#FFF8F1] focus:outline-none focus:bg-white"
            style={{ fontSize: "16px" }}
          />
          <button
            type="submit"
            disabled={sending || !input.trim()}
            onMouseDown={(e) => e.preventDefault()}
            data-testid={`${testPrefix}-send`}
            aria-label="Envoyer"
            className="h-11 w-11 flex-none rounded-xl bg-primary text-primary-foreground border-2 border-black grid place-items-center shadow-[0_3px_0_0_#000] active:translate-y-[2px] active:shadow-[0_1px_0_0_#000] transition-all disabled:opacity-50"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" strokeWidth={3} />}
          </button>
        </form>
      </div>
    </div>
  );
}

function SupportBubble({ m, mine, status, testPrefix }) {
  return (
    <div className={"flex flex-col " + (mine ? "items-end" : "items-start")} data-testid={`${testPrefix}-msg-${mine ? "mine" : "other"}`}>
      <div className={`max-w-[78%] rounded-2xl px-3 py-2 street-border-thin ${mine ? "bg-[#DCF8C6] rounded-br-sm" : "bg-white rounded-bl-sm"} ${m._optimistic ? "opacity-70" : ""}`}>
        <div className="font-[Outfit] text-[13px] font-medium leading-snug whitespace-pre-wrap break-words">{m.body}</div>
        <div className="mt-0.5 text-[10px] text-right text-foreground/50">{fmtTime(m.created_at)}</div>
      </div>
      {status && (
        <div className="mt-0.5 px-1 flex items-center gap-1 font-[Outfit] text-[10px] font-bold text-neutral-500" data-testid={`${testPrefix}-status-${status}`}>
          {status === "seen" && <><CheckCheck className="h-3 w-3 text-primary" strokeWidth={3} />Vu</>}
          {status === "sent" && <><Check className="h-3 w-3" strokeWidth={3} />Envoyé</>}
          {status === "sending" && "Envoi…"}
        </div>
      )}
    </div>
  );
}
